import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * <buy-now-button> — "Buy Now" next to Add to cart (design: gold button,
 * Desktop/barkat/image2). blocks/custom-buy-now.liquid renders it inside the
 * buy-buttons form row — on the product page and in the quick-add pop-up
 * (this module is loaded on every page by snippets/custom-globals.liquid,
 * because scripts inside injected pop-up HTML never run).
 *
 * Buys ONLY this product, like Amazon's Buy Now (decided 2026-10-15): the
 * shopper's cart is not part of that checkout and is still there afterwards.
 * Shopify has one cart per visitor (a second cart needs a Storefront API
 * token), and its single-product checkout link REPLACES that cart (tested),
 * so instead:
 *   1. snapshot the cart (items with options and subscription plans, note,
 *      attributes) into localStorage;
 *   2. empty it and add just the form's selection (the SAME product form Add
 *      to cart submits: variant, quantity, line-item properties, plan);
 *   3. open checkout: it holds this one product;
 *   4. on the next store page (paid, or came back without paying), put the
 *      snapshot back and drop the Buy Now line: the cart is exactly as it was.
 * Its disabled state mirrors the Add to cart button, which Horizon already
 * updates for every variant change (sold out, unavailable).
 *
 * Paying twice is the costly mistake, so every repeat path is covered:
 * - Double / impatient clicks: one request; the button is busy (spinner) and
 *   Add to cart is locked too (form.dataset.qAdding, the same lock as
 *   assets/custom-add-guard.js), so the two can't race.
 * - Back from checkout: the browser may restore this page from its cache with
 *   the button still busy; `pageshow` unlocks it and restores the cart.
 * - Buy Now again after coming back: the restore runs first (buy() waits for
 *   it), so the cart is the shopper's own again and the new checkout again
 *   holds only the product chosen now.
 * - Offline, slow network (15 s timeout), sold out or quantity limits: a clear
 *   message under the button (Shopify's own text for cart errors) and the
 *   button is usable again.
 *
 * Messages: data-msg-offline / data-msg-timeout / data-msg-failed, from
 * locales custom.ui.pay_*.
 *
 * @extends {Component<{ button: HTMLButtonElement }>}
 */

const TIMEOUT_MS = 15000;
const SNAPSHOT_KEY = 'q-buy-now-cart';
const SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The cart as it was before a Buy Now, and the line Buy Now put there
 * @typedef {{
 *   items: { id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[],
 *   note: string,
 *   attributes: Record<string, string>,
 *   bought: { variant: number, quantity: number },
 *   time: number
 * }} CartSnapshot
 */

/** @returns {CartSnapshot | null} */
const readSnapshot = () => {
  try {
    const snapshot = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null');
    if (!snapshot || Date.now() - snapshot.time > SNAPSHOT_MAX_AGE_MS) return null;
    return snapshot;
  } catch {
    return null;
  }
};

/** @param {CartSnapshot | null} snapshot */
const writeSnapshot = (snapshot) => {
  try {
    if (snapshot) localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
    else localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // Storage blocked: nothing to restore later (handled in #checkoutOnly)
  }
};

/**
 * fetch with a time limit, so a stalled network never leaves the button busy
 * @param {string} url
 * @param {RequestInit} [init]
 */
const fetchJson = async (url, init = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      ...init,
      headers: { Accept: 'application/json', ...init.headers },
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || response.statusText);
      error.name = 'CartError';
      throw error;
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Puts back the cart a Buy Now set aside: drops the Buy Now line if it is still
 * there unpaid, adds the shopper's own items again, restores note and
 * attributes, then refreshes the cart drawer and counts on screen.
 * Runs once per snapshot, on the first store page after checkout.
 * @type {Promise<void> | null}
 */
let restoring = null;

function restoreCart() {
  restoring ??= (async () => {
    const snapshot = readSnapshot();
    if (!snapshot) return;
    writeSnapshot(null); // one attempt: never restore twice

    const cart = await fetchJson(Theme.routes.cart_url + '.js');
    const onlyBuyNow =
      cart.items.length === 1 &&
      cart.items[0].variant_id === snapshot.bought.variant &&
      cart.items[0].quantity === snapshot.bought.quantity;
    // Not paid: the Buy Now product is not part of the shopper's cart, take it out
    if (onlyBuyNow) await fetchJson(Theme.routes.cart_url + '/clear.js', { method: 'POST' });

    if (snapshot.items.length) {
      await fetchJson(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: snapshot.items }),
      });
    }
    if (snapshot.note || Object.keys(snapshot.attributes).length) {
      await fetchJson(Theme.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: snapshot.note, attributes: snapshot.attributes }),
      });
    }
    await refreshCartOnScreen();
  })().catch((error) => console.warn('[buy-now] cart restore', error));
  return restoring;
}

/** The page was drawn with the Buy Now cart: bring drawer, cart page and counts up to date */
async function refreshCartOnScreen() {
  if (Theme.template?.name === 'cart') {
    window.location.reload();
    return;
  }
  const cart = await fetchJson(Theme.routes.cart_url + '.js');
  if (document.getElementById('shopify-section-cart-drawer-section')) {
    await sectionRenderer.renderSection('cart-drawer-section', { cache: false }).catch(() => {});
  }
  for (const icon of document.querySelectorAll('cart-icon')) {
    /** @type {any} */ (icon).renderCartBubble?.(cart.item_count, false);
  }
}

class BuyNowButton extends Component {
  requiredRefs = ['button'];

  /** @type {MutationObserver | undefined} */
  #observer;

  /** True from the click until checkout opens (or the attempt fails) */
  #busy = false;

  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    this.#release();
    if (event.persisted) restoreCart(); // back from checkout through the back-forward cache
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', this.#onPageShow);

    const addToCart = this.#addToCartButton;
    if (!addToCart) return;

    const sync = () => {
      // While busy, Add to cart is locked on purpose; don't copy that
      if (!this.#busy) this.refs.button.disabled = addToCart.disabled;
    };
    sync();
    this.#observer = new MutationObserver(sync);
    this.#observer.observe(addToCart, { attributes: true, attributeFilter: ['disabled'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#observer?.disconnect();
  }

  /** The product form this button belongs to */
  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.closest('form'));
  }

  get #addToCartButton() {
    return /** @type {HTMLButtonElement | null} */ (this.#form?.querySelector('button[name="add"]') ?? null);
  }

  async buy() {
    const form = this.#form;
    const { button } = this.refs;
    // Already on its way to checkout, or Add to cart is mid-request
    if (!form || this.#busy || button.disabled || form.dataset.qAdding === 'true') return;

    this.#showError('');
    // Let the browser show its own messages for invalid quantity, required properties…
    if (!form.reportValidity()) return;

    if (!navigator.onLine) {
      this.#showError(this.dataset.msgOffline);
      return;
    }

    this.#lock();

    try {
      await restoreCart(); // a previous Buy Now's cart first
      await this.#checkoutOnly(form);
      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    } catch (error) {
      const message =
        error instanceof Error && error.name === 'CartError'
          ? error.message
          : error instanceof Error && error.name === 'AbortError'
            ? this.dataset.msgTimeout
            : navigator.onLine
              ? this.dataset.msgFailed
              : this.dataset.msgOffline;
      this.#showError(message || this.dataset.msgFailed);
      this.#release();
    }
  }

  /**
   * Leaves only the form's selection in the cart, after saving the cart so it
   * can be put back (restoreCart). If nothing can be saved (storage blocked),
   * the cart is kept and the product is added to it: never lose the cart.
   * @param {HTMLFormElement} form
   */
  async #checkoutOnly(form) {
    const body = new FormData(form);
    const variant = Number(body.get('id'));
    const quantity = Math.max(1, Number(body.get('quantity')) || 1);

    const cart = await fetchJson(Theme.routes.cart_url + '.js');
    // Always a snapshot, even of an empty cart: an unpaid Buy Now product must
    // not be left behind in the shopper's cart
    {
      /** @type {CartSnapshot} */
      const snapshot = {
        items: cart.items.map((/** @type {any} */ item) => ({
          id: item.variant_id,
          quantity: item.quantity,
          ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {}),
          ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}),
        })),
        note: cart.note || '',
        attributes: cart.attributes || {},
        bought: { variant, quantity },
        time: Date.now(),
      };
      writeSnapshot(snapshot);
      if (!readSnapshot()) {
        await fetchJson(Theme.routes.cart_add_url, { method: 'POST', body });
        return;
      }
      if (cart.items.length) await fetchJson(Theme.routes.cart_url + '/clear.js', { method: 'POST' });
    }
    try {
      await fetchJson(Theme.routes.cart_add_url, { method: 'POST', body });
    } catch (error) {
      // Could not add (sold out meanwhile, a limit): give the shopper their cart back
      restoring = null;
      await restoreCart();
      throw error;
    }
  }

  #lock() {
    const form = this.#form;
    const { button } = this.refs;
    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (form) {
      // Same flag custom-add-guard.js uses: Add to cart submits are ignored meanwhile
      form.dataset.qAdding = 'true';
      form.dataset.qBuying = 'true';
    }
  }

  #release() {
    if (!this.#busy) return;
    const form = this.#form;
    this.#busy = false;
    this.refs.button.removeAttribute('aria-busy');
    if (form?.dataset.qBuying === 'true') {
      delete form.dataset.qBuying;
      delete form.dataset.qAdding;
    }
    this.refs.button.disabled = this.#addToCartButton?.disabled ?? false;
  }

  /** @param {string | undefined} message */
  #showError(message) {
    const region = this.querySelector('[role="alert"]');
    if (region) region.textContent = message || '';
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}

// First store page after a Buy Now checkout (paid or not): the shopper's cart comes back
if (readSnapshot()) restoreCart();
